import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Box, CircularProgress,
  TextField, MenuItem, IconButton, Button, Typography, Slide, Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Customer } from './models/Customer';

interface CustomerFilters {
  name: string;
  recommender: string;
  status: string;
  phone: string;
  rowsPerPage: number | 'all';
}

export default function CustomerTable() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Customer | null>(null);

  const [filters, setFilters] = useState<CustomerFilters>(() => {
    const saved = localStorage.getItem('customerFilters');
    return saved
      ? JSON.parse(saved)
      : { name: '', recommender: '', status: '', phone: '', rowsPerPage: 25 };
  });

  const navigate = useNavigate();

  const cellSx = { py: 1, textAlign: 'center' as const, whiteSpace: 'nowrap' as const };
  const headerCellSx = { fontWeight: 'bold', textAlign: 'center' as const, whiteSpace: 'nowrap' as const };

  const statusMap: Record<string, string> = {
    Single: 'רווק',
    Married: 'נשוי',
    Divorced: 'גרוש',
    Widowed: 'אלמן',
    Unknown: 'לא ידוע',
  };

  const healthFundMap: Record<string, string> = {
    Unknown: 'לא ידוע',
    Maccabi: 'מכבי',
    Leumit: 'לאומית',
    Clalit: 'כללית',
    Meuchedet: 'מאוחדת',
  };

  // סטטוס לקוח → צבעים
  const customerStatusColors: Record<string, string> = {
    "מועדף": "red",
    "בטיפול": "orange",        // yellow is hard to read, orange is clearer
    "הוגש לוועדה": "blue",
    "נקבע לו וועדה": "green",
    "צריך מעקב": "black",
    "לא ידוע": "gray",
  };

  // Persist filters
  useEffect(() => {
    localStorage.setItem('customerFilters', JSON.stringify(filters));
  }, [filters]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/customers', {
        params: {
          name: filters.name,
          recommender: filters.recommender,
          status: filters.status,
          phone: filters.phone,
          limit: filters.rowsPerPage === 'all' ? undefined : filters.rowsPerPage,
        },
      });
      if (res && res.data) setCustomers(res.data);
    } catch (err) {
      console.error('Failed to fetch customers', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleEdit = (customerId: string) => navigate(`/customers/edit/${customerId}`);

  const handleDelete = async (customerId: string) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק לקוח זה?')) return;
    try {
      await api.delete(`/customers/${customerId}`);
      await fetchCustomers();
      setSelected((curr) => (curr?.id === customerId ? null : curr));
    } catch (err) {
      console.error('מחיקת לקוח נכשלה', err);
    }
  };

  const handleRowClick = (row: Customer) => setSelected(row);
  const closeDetails = () => setSelected(null);

  // Close with Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeDetails(); };
    if (selected) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  // Auto-close if selected disappears due to filters/deletion
  useEffect(() => {
    if (selected && !customers.some((c) => c.id === selected.id)) setSelected(null);
  }, [customers, selected]);

  const selectedStatusLabel = useMemo(
    () => (selected ? statusMap[selected.status] ?? 'לא ידוע' : ''),
    [selected]
  );

  // ===== Scroll-synced details overlay =====
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [panelTop, setPanelTop] = useState(0);
  const [panelHeight, setPanelHeight] = useState<number | string>('100%');

  useEffect(() => {
    const el = containerRef.current;
    if (!selected || !el) return;

    const sync = () => {
      setPanelTop(el.scrollTop);
      setPanelHeight(el.clientHeight);
    };

    sync();
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [selected]);

  const exportToCSV = () => {
    if (!customers.length) {
      alert("אין נתונים לייצוא");
      return;
    }

    const headers = [
      "שם",
      "סטטוס לקוח",   // new column
      "טלפון",
      "תעודת זהות",
      "תאריך לידה",
      "תאריך הנפקת ת.ז.",
      "תאריך נישואין",
      "כתובת",
      "עיר",
      "סטטוס",
      "קופת חולים",
      "ממליץ",
    ];

    const rows = customers.map((c) => [
      c.name,
      c.customerStatus ?? "לא ידוע",   // new field
      c.phone,
      c.idNumber,
      c.dateOfBirth,
      c.idIssueDate,
      c.marriageDate,
      c.address,
      c.city,
      statusMap[c.status] ?? "לא ידוע",
      healthFundMap[c.healthFund] ?? "לא ידוע",
      c.recommenderName,
    ]);

    const csvContent =
      [headers, ...rows].map((e) => e.map((v) => `"${v ?? ""}"`).join(",")).join("\n");

    // Add BOM for Hebrew/UTF-8 Excel support
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "דוח_לקוחות.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  return (
    <Box display="flex" flexDirection="column" height="calc(100vh - 25px)">
      {/* Bottom controls: filters + rows-per-page */}
      <Box
        flexShrink={0}
        p={2}
        borderTop="1px solid #ccc"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="#fafafa"
      >
        {/* Right side: filters */}
        <Box display="flex" gap={2}>
          <Box flexShrink={0} p={0} display="flex" gap={2} alignItems="center">
            <TextField
              label="חיפוש לפי שם"
              variant="outlined"
              size="small"
              value={filters.name}
              onChange={(e) => setFilters((f) => ({ ...f, name: e.target.value }))}
            />
            <TextField
              label="חיפוש לפי ממליץ"
              variant="outlined"
              size="small"
              value={filters.recommender}
              onChange={(e) => setFilters((f) => ({ ...f, recommender: e.target.value }))}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="סטטוס"
              variant="outlined"
              size="small"
              select
              value={filters.status}
              onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
              sx={{ minWidth: 120 }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="Single">רווק</MenuItem>
              <MenuItem value="Married">נשוי</MenuItem>
              <MenuItem value="Divorced">גרוש</MenuItem>
              <MenuItem value="Widowed">אלמן</MenuItem>
            </TextField>
            <TextField
              label="חיפוש לפי טלפון"
              variant="outlined"
              size="small"
              value={filters.phone}
              onChange={(e) => setFilters((f) => ({ ...f, phone: e.target.value }))}
              sx={{ minWidth: 180 }}
            />

            <Button variant="contained" color="primary" onClick={exportToCSV}>
              הפק דוח
            </Button>
          </Box>
        </Box>

        {/* Left side: rows per page */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2">הצג</Typography>
          <TextField
            size="small"
            select
            value={filters.rowsPerPage}
            onChange={(e) => {
              const value = e.target.value as string;
              setFilters((f) => ({
                ...f,
                rowsPerPage: value === 'all' ? 'all' : parseInt(value, 10),
              }));
            }}
            sx={{ minWidth: 80 }}
          >
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={15}>15</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
            <MenuItem value={100}>100</MenuItem>
            <MenuItem value={150}>150</MenuItem>
            <MenuItem value={200}>200</MenuItem>
            <MenuItem value="all">הכל</MenuItem>
          </TextField>
          <Typography variant="body2">פריטים</Typography>
        </Box>
      </Box>

      {/* Table area */}
      <Box flex="1 1 auto" position="relative" sx={{ minHeight: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ height: '100%', position: 'relative', overflow: 'auto' }}
            ref={containerRef}
          >
            {/* Click-catcher aligned to the visible area */}
{selected && (
  <Box
    onClick={closeDetails}
    aria-label="close details by clicking outside"
    role="button"
    tabIndex={0}
    sx={{
      position: 'absolute',
      right: 0,
      top: panelTop,
      height: panelHeight,
      width: { xs: 0, md: '50%' },
      backgroundColor: 'rgba(0,0,0,0.04)',
      zIndex: 11,
      outline: 'none',
    }}
  />
)}

{/* Sliding details panel aligned to the visible area */}
<Slide in={!!selected} direction="left" mountOnEnter unmountOnExit>
  <Box
    sx={{
      position: 'absolute',
      right: 0,
      top: panelTop,
      height: panelHeight,
      width: { xs: '100%', md: '50%' }, // << 70% wide
      zIndex: 12,
      p: 1.5,
    }}
  >
  <Paper elevation={4} sx={{ height: '100%', p: 2, overflow: 'auto' }}>
  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <Typography variant="h6" fontWeight="bold">
      פרטי לקוח
    </Typography>

    <Box display="flex" gap={1}>
      {selected && (
        <>
          <Button
            variant="contained"
            size="small"
            onClick={() => handleEdit(selected.id)}
          >
            עריכה
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => handleDelete(selected.id)}
          >
            מחיקה
          </Button>
        </>
      )}
      <IconButton onClick={closeDetails} aria-label="סגור">
        <CloseIcon />
      </IconButton>
    </Box>
  </Box>


      {selected && (
        <Box display="grid" gridTemplateColumns="1fr" gap={2}>
          {/* Personal */}
          <Section title="פרטים אישיים" />
          <ThreeCol>
            <DetailItem label="שם" value={selected.name} />
            <DetailItem label="סטטוס לקוח" value={selected.customerStatus} />
            <DetailItem label="טלפון" value={selected.phone} />
            <DetailItem label="טלפון נוסף" value={selected.additionalPhone} />
            <DetailItem label="ת.ז." value={selected.idNumber} />
            <DetailItem label="תאריך לידה" value={selected.dateOfBirth} />
            <DetailItem label="תאריך הנפקת ת.ז." value={selected.idIssueDate} />
            <DetailItem label="תאריך נישואין" value={selected.marriageDate} />
            <DetailItem label="כתובת" value={selected.address} />
            <DetailItem label="עיר" value={selected.city} />
            <DetailItem label="סטטוס" value={selectedStatusLabel} />
            <DetailItem label="סכום השתכרות בתלוש" value={num(selected.payslipIncome)} />
            <DetailItem label="שם משתמש" value={selected.username} />
            <DetailItem label="סיסמה" value={selected.password} />
            <DetailItem
              label="קופת חולים"
              value={healthFundMap[selected.healthFund ?? 'Unknown']}
            />
            <DetailItem label="מייל שהוקם" value={selected.createdEmail} />
            <DetailItem label="סיסמה למייל" value={selected.createdEmailPassword} />
            <DetailItem label="כתובת חדשה" value={selected.newAddress} />
            <DetailItem label="עיר חדשה" value={selected.newCity} />
            <DetailItem label="קוד הזדהות ממשלתית" value={selected.governmentAuthCode} />
            <DetailItem label="מספר דרכון" value={selected.passportNumber} />
            <DetailItem label="תאריך הנפקת דרכון" value={selected.passportIssueDate} />
          </ThreeCol>

          <Divider />

          {/* Spouse */}
          <Section title="פרטי בת זוג" />
          <ThreeCol>
            <DetailItem label="שם" value={selected.spouseName} />
            <DetailItem label="טלפון" value={selected.spousePhone} />
            <DetailItem label="ת.ז." value={selected.spouseIdNumber} />
            <DetailItem label="תאריך לידה" value={selected.spouseDateOfBirth} />
            <DetailItem label="תאריך הנפקת ת.ז." value={selected.spouseIdIssueDate} />
            <DetailItem label="סכום השתכרות בתלוש" value={num(selected.spousePayslipIncome)} />
            <DetailItem label="שם משתמש" value={selected.spouseUsername} />
            <DetailItem label="סיסמה" value={selected.spousePassword} />
            <DetailItem label="קוד הזדהות ממשלתית" value={selected.spouseGovernmentAuthCode} />
          </ThreeCol>

          <Divider />

          {/* General */}
          <Section title="פרטים כלליים" />
          <ThreeCol>
            <DetailItem label="בנק" value={selected.bank} />
            <DetailItem label="סניף" value={selected.branch} />
            <DetailItem label="מס' ח-ן" value={selected.accountNumber} />
            <DetailItem label="מס' ילדים" value={num(selected.numberOfChildren)} />
            <DetailItem label="תאריך לידה ילד אחד לפני אחרון" value={selected.secondToLastChildBirthDate} />
            <DetailItem label="תאריך לידה ילד אחרון" value={selected.lastChildBirthDate} />
            <DetailItem label="שם ממליץ" value={selected.recommenderName} />
            <DetailItem label="ביקור רופא 1" value={selected.doctorVisit1} />
            <DetailItem label="ביקור רופא 2" value={selected.doctorVisit2} />
          </ThreeCol>
        </Box>
      )}
    </Paper>
  </Box>
</Slide>


{/* The table itself */}
            {/* Table */}
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>שם</TableCell>
                  <TableCell sx={headerCellSx}>סטטוס לקוח</TableCell> {/* new */}
                  <TableCell sx={headerCellSx}>טלפון</TableCell>
                  <TableCell sx={headerCellSx}>ת.ז.</TableCell>
                  <TableCell sx={headerCellSx}>תאריך לידה</TableCell>
                  <TableCell sx={headerCellSx}>תאריך הנפקת ת.ז.</TableCell>
                  <TableCell sx={headerCellSx}>תאריך נישואין</TableCell>
                  <TableCell sx={headerCellSx}>כתובת</TableCell>
                  <TableCell sx={headerCellSx}>עיר</TableCell>
                  <TableCell sx={headerCellSx}>סטטוס</TableCell>
                  <TableCell sx={headerCellSx}>קופת חולים</TableCell>
                  <TableCell sx={headerCellSx}>ממליץ</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map((row) => {
                  const isSelected = selected?.id === row.id;
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleRowClick(row)}
                      sx={{
                        cursor: 'pointer',
                        '&.Mui-selected td': {
                          backgroundColor: (theme) => theme.palette.action.selected,
                        },
                      }}
                    >
                      <TableCell sx={cellSx}>{row.name}</TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          fontWeight: 'bold',
                          color: customerStatusColors[row.customerStatus ?? "לא ידוע"],
                        }}
                      >
                        {row.customerStatus ?? "לא ידוע"}
                      </TableCell>
                      <TableCell sx={cellSx}>{row.phone}</TableCell>
                      <TableCell sx={cellSx}>{row.idNumber}</TableCell>
                      <TableCell sx={cellSx}>{row.dateOfBirth}</TableCell>
                      <TableCell sx={cellSx}>{row.idIssueDate}</TableCell>
                      <TableCell sx={cellSx}>{row.marriageDate}</TableCell>
                      <TableCell sx={cellSx}>{row.address}</TableCell>
                      <TableCell sx={cellSx}>{row.city}</TableCell>
                      <TableCell sx={cellSx}>{statusMap[row.status] ?? 'לא ידוע'}</TableCell>
                      <TableCell sx={cellSx}>
                        {healthFundMap[row.healthFund] ?? 'לא ידוע'}
                      </TableCell>
                      <TableCell sx={cellSx}>{row.recommenderName}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
}

/** Utilities & small helpers */
function num(v?: number | null) {
  return (v ?? '') === '' ? '-' : String(v);
}

function Section({ title }: { title: string }) {
  return (
    <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 0.5 }}>
      {title}
    </Typography>
  );
}

function TwoCol({ children }: { children: React.ReactNode }) {
  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" columnGap={1.5} rowGap={1}>
      {children}
    </Box>
  );
}

function ThreeCol({ children }: { children: React.ReactNode }) {
  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" columnGap={1.5} rowGap={1.5}>
      {children}
    </Box>
  );
}

function DetailItem({ label, value }: { label: string; value?: string | number | null }) {
  const content = (value ?? '') !== '' ? value : '-';
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{content}</Typography>
    </Box>
  );
}
